import type { Playlist } from "@/types/types";

export interface CachedSessionInfo {
    id: string;
    [key: string]: unknown;
}

export interface CachedSessionSnapshot {
    playlist: Playlist;
    sessionInfo: CachedSessionInfo;
    sessionId: string;
}

interface ReadCachedSessionSnapshotOptions {
    expectedSessionId: string;
    storedSessionId: string | null;
    serializedPlaylist: string | null;
    serializedSessionInfo: string | null;
}

const parseRecord = (serializedValue: string | null): Record<string, unknown> | null => {
    if (!serializedValue) return null;

    try {
        const parsedValue = JSON.parse(serializedValue);
        return parsedValue && typeof parsedValue === "object" && !Array.isArray(parsedValue)
            ? parsedValue as Record<string, unknown>
            : null;
    } catch {
        return null;
    }
};

const normalizeIdentifier = (value: unknown): string =>
    String(value ?? "").trim();

export const readCachedSessionSnapshot = ({
    expectedSessionId,
    storedSessionId,
    serializedPlaylist,
    serializedSessionInfo,
}: ReadCachedSessionSnapshotOptions): CachedSessionSnapshot | null => {
    const normalizedSessionId = normalizeIdentifier(expectedSessionId);
    if (
        !normalizedSessionId ||
        normalizeIdentifier(storedSessionId) !== normalizedSessionId
    ) {
        return null;
    }

    const playlistRecord = parseRecord(serializedPlaylist);
    const sessionInfoRecord = parseRecord(serializedSessionInfo);
    if (!playlistRecord || !sessionInfoRecord) return null;

    const playlistId = normalizeIdentifier(playlistRecord.id);
    const sessionInfoId = normalizeIdentifier(sessionInfoRecord.id);
    const playlistTitle = String(playlistRecord.title ?? "").trim();
    const playlistSongs = playlistRecord.songs;

    if (
        !playlistId ||
        !playlistTitle ||
        sessionInfoId !== normalizedSessionId ||
        !Array.isArray(playlistSongs) ||
        playlistSongs.some((song) => !song || typeof song !== "object" || Array.isArray(song))
    ) {
        return null;
    }

    return {
        playlist: {
            ...playlistRecord,
            id: playlistId,
            title: playlistTitle,
            songs: playlistSongs,
        } as Playlist,
        sessionInfo: {
            ...sessionInfoRecord,
            id: sessionInfoId,
        },
        sessionId: normalizedSessionId,
    };
};
